import React, { useState, useEffect } from 'react';
import { ref, onValue, set } from 'firebase/database';
import { db } from '../firebase';

// Converts a Firebase juice key like "juice_aam_panna" → "Aam Panna"
function formatJuiceName(juiceId) {
  return juiceId
    .replace(/^juice_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const DEFAULT_SETTINGS = {
  enabled: false,
  end_date: '',
  prices: {},
};

export default function PromoControlPanel() {
  // All kiosk IDs from Firebase inventory
  const [kioskIds, setKioskIds] = useState([]);
  // Currently selected kiosk
  const [selectedKiosk, setSelectedKiosk] = useState('');
  // Juice IDs that belong to the selected kiosk (from inventory)
  const [kioskJuices, setKioskJuices] = useState([]);
  // Promo settings being edited for the selected kiosk
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  // Live data from Firebase for the selected kiosk
  const [liveData, setLiveData] = useState(null);

  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Load kiosk list from inventory ──────────────────────────────────────────
  useEffect(() => {
    const inventoryRef = ref(db, 'inventory');
    const unsub = onValue(inventoryRef, (snap) => {
      const data = snap.val();
      if (data) {
        const ids = Object.keys(data);
        setKioskIds(ids);
        // Auto-select first kiosk if none selected yet
        setSelectedKiosk((prev) => prev || ids[0] || '');
      }
    });
    return unsub;
  }, []);

  // ── When selected kiosk changes: load its juice list from inventory ──────────
  useEffect(() => {
    if (!selectedKiosk) return;
    const invRef = ref(db, `inventory/${selectedKiosk}`);
    const unsub = onValue(invRef, (snap) => {
      const data = snap.val();
      if (data) {
        setKioskJuices(Object.keys(data));
      } else {
        setKioskJuices([]);
      }
    });
    return unsub;
  }, [selectedKiosk]);

  // ── When selected kiosk changes: load its promo settings ────────────────────
  useEffect(() => {
    if (!selectedKiosk) return;
    setSettings(DEFAULT_SETTINGS);
    setLiveData(null);

    const promoRef = ref(db, `promo_settings/${selectedKiosk}`);
    const unsub = onValue(promoRef, (snap) => {
      const data = snap.val();
      if (data) {
        setLiveData(data);
        setSettings({
          enabled: data.enabled ?? false,
          end_date: data.end_date ?? '',
          prices: data.prices ?? {},
        });
      } else {
        setSettings(DEFAULT_SETTINGS);
        setLiveData(null);
      }
    });
    return unsub;
  }, [selectedKiosk]);

  // ── Save promo settings for the selected kiosk ───────────────────────────────
  const handleSave = async () => {
    if (!selectedKiosk) return;
    setSaving(true);
    try {
      await set(ref(db, `promo_settings/${selectedKiosk}`), {
        enabled: settings.enabled,
        end_date: settings.end_date,
        prices: settings.prices,
        updated_at: new Date().toISOString(),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      alert('Failed to save promo settings: ' + e.message);
    }
    setSaving(false);
  };

  const handlePriceChange = (juiceId, value) => {
    setSettings((prev) => ({
      ...prev,
      prices: { ...prev.prices, [juiceId]: Number(value) },
    }));
  };

  const isExpired = settings.end_date && new Date(settings.end_date) < new Date();
  const effectivelyActive = settings.enabled && !isExpired;

  return (
    <div className="section">
      <div className="section-header">
        <h2 className="section-title">
          <span className="section-title-icon">🎉</span>
          Promo Price Control
          <span style={{
            marginLeft: '0.75rem', fontSize: '0.75rem', fontWeight: 500,
            padding: '2px 10px', borderRadius: '999px',
            background: effectivelyActive ? 'rgba(0,255,153,0.15)' : 'rgba(255,68,68,0.15)',
            color: effectivelyActive ? '#00ff99' : '#ff6b6b',
          }}>
            {effectivelyActive ? '🟢 PROMO LIVE' : isExpired ? '⏰ EXPIRED' : '⭕ OFF'}
          </span>
        </h2>
        <p style={{ color: '#666', fontSize: '0.8rem', margin: 0 }}>
          Each kiosk has independent promo prices — kiosk reads its own node from Firebase
        </p>
      </div>

      {/* ── Kiosk selector ──────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '1.25rem' }}>
        <label style={{ display: 'block', color: '#aaa', fontSize: '0.8rem', marginBottom: '0.4rem' }}>
          Select Kiosk
        </label>
        {kioskIds.length === 0 ? (
          <p style={{ color: '#666', fontSize: '0.85rem' }}>
            No kiosks found in inventory. Make sure at least one kiosk has reported inventory data.
          </p>
        ) : (
          <select
            value={selectedKiosk}
            onChange={(e) => setSelectedKiosk(e.target.value)}
            style={{
              background: '#1a1a2e',
              border: '1px solid #2a2a4a',
              borderRadius: '0.5rem',
              color: 'white',
              padding: '0.5rem 0.75rem',
              fontSize: '0.9rem',
              minWidth: '220px',
              cursor: 'pointer',
            }}
          >
            {kioskIds.map((id) => (
              <option key={id} value={id}>{id}</option>
            ))}
          </select>
        )}
      </div>

      {selectedKiosk && (
        <>
          {/* ── Enable toggle ──────────────────────────────────────────────── */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '1rem',
            padding: '1rem', background: 'rgba(255,255,255,0.03)',
            borderRadius: '0.75rem', marginBottom: '1rem',
            border: `1px solid ${effectivelyActive ? 'rgba(0,255,153,0.2)' : 'rgba(255,255,255,0.05)'}`,
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
              <div
                onClick={() => setSettings((p) => ({ ...p, enabled: !p.enabled }))}
                style={{
                  width: '44px', height: '24px', borderRadius: '12px',
                  background: settings.enabled ? '#00ff99' : '#333',
                  position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                }}
              >
                <div style={{
                  position: 'absolute', top: '3px',
                  left: settings.enabled ? '23px' : '3px',
                  width: '18px', height: '18px', borderRadius: '50%',
                  background: 'white', transition: 'left 0.2s',
                }} />
              </div>
              <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                {settings.enabled ? 'Promo Enabled' : 'Promo Disabled'}
              </span>
            </label>
            {isExpired && settings.enabled && (
              <span style={{ color: '#ffa500', fontSize: '0.8rem' }}>
                ⚠️ End date has passed — kiosk will use regular prices
              </span>
            )}
          </div>

          {/* ── End date ───────────────────────────────────────────────────── */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', color: '#aaa', fontSize: '0.8rem', marginBottom: '0.4rem' }}>
              Promo End Date &amp; Time
            </label>
            <input
              type="datetime-local"
              value={settings.end_date ? settings.end_date.slice(0, 16) : ''}
              onChange={(e) => setSettings((p) => ({ ...p, end_date: e.target.value + ':00' }))}
              style={{
                background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: '0.5rem',
                color: 'white', padding: '0.5rem 0.75rem', fontSize: '0.9rem',
              }}
            />
            {settings.end_date && (
              <span style={{ marginLeft: '0.75rem', color: isExpired ? '#ff6b6b' : '#00ff99', fontSize: '0.8rem' }}>
                {isExpired ? '⏰ Expired' : `✅ Active until ${new Date(settings.end_date).toLocaleString('en-IN')}`}
              </span>
            )}
          </div>

          {/* ── Promo prices (dynamic per kiosk) ───────────────────────────── */}
          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ color: '#aaa', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
              Promo Prices (₹) for <strong style={{ color: 'var(--text-primary)' }}>{selectedKiosk}</strong>
            </p>
            {kioskJuices.length === 0 ? (
              <p style={{ color: '#666', fontSize: '0.85rem' }}>
                No juices found for this kiosk yet.
              </p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                {kioskJuices.map((juiceId) => (
                  <div key={juiceId} style={{
                    background: 'rgba(255,255,255,0.03)', border: '1px solid #2a2a4a',
                    borderRadius: '0.75rem', padding: '0.875rem',
                  }}>
                    <label style={{ display: 'block', color: '#aaa', fontSize: '0.75rem', marginBottom: '0.4rem' }}>
                      🧃 {formatJuiceName(juiceId)}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ color: '#666' }}>₹</span>
                      <input
                        type="number"
                        min="1"
                        max="999"
                        value={settings.prices[juiceId] ?? ''}
                        placeholder="—"
                        onChange={(e) => handlePriceChange(juiceId, e.target.value)}
                        style={{
                          width: '70px', background: '#1a1a2e', border: '1px solid #2a2a4a',
                          borderRadius: '0.4rem', color: '#00ff99', fontWeight: 700,
                          fontSize: '1.1rem', padding: '0.3rem 0.5rem', textAlign: 'center',
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Save button ─────────────────────────────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                background: saving ? '#333' : 'linear-gradient(135deg, #00ff99, #00bfff)',
                color: saving ? '#888' : '#000',
                border: 'none', borderRadius: '0.6rem',
                padding: '0.6rem 1.5rem', fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem', transition: 'all 0.2s',
              }}
            >
              {saving ? 'Saving…' : `💾 Save & Push to ${selectedKiosk}`}
            </button>
            {saved && (
              <span style={{ color: '#00ff99', fontSize: '0.85rem' }}>
                ✅ Saved! Kiosk will apply within 60 seconds.
              </span>
            )}
          </div>

          {/* ── Last updated ────────────────────────────────────────────────── */}
          {liveData?.updated_at && (
            <p style={{ color: '#444', fontSize: '0.75rem', marginTop: '0.75rem' }}>
              Last updated: {new Date(liveData.updated_at).toLocaleString('en-IN')}
            </p>
          )}
        </>
      )}
    </div>
  );
}
